/**
 * Factory for creating endpoint client instances (WebSocket or REST-based)
 */

import { createWebSocketClient, type WebSocketClient } from './websocket';
import { DiscordClient } from './discordClient';
import type { SavedEndpoint } from '@/types/endpoint';
import { isFoundryCredentials, isDiscordCredentials, isStandaloneCredentials, isPlayerCredentials } from '@/types/endpoint';
import { useAuthStore } from '@/stores/authStore';
import { WS_URL } from '@/config/api';

const SERVICE_TAG = '[CONNECTION_FACTORY]';

/** Union of all endpoint client types */
export type EndpointClient = WebSocketClient | DiscordClient;

/**
 * Create an endpoint client appropriate for the endpoint type
 */
export function createEndpointConnection(endpoint: SavedEndpoint): EndpointClient {
  console.info(`${SERVICE_TAG} Creating connection for endpoint`, {
    id: endpoint.id,
    name: endpoint.name,
    type: endpoint.type
  });

  if (endpoint.type === 'discord') {
    return new DiscordClient(endpoint.id);
  }

  return createWebSocketClient(endpoint.id);
}

/**
 * Extract connection parameters from endpoint credentials
 */
export function extractConnectionParams(endpoint: SavedEndpoint): Record<string, unknown> | null {
  if (endpoint.type === 'foundry') {
    if (!isFoundryCredentials(endpoint.credentials)) {
      console.error(`${SERVICE_TAG} Invalid Foundry credentials`, { endpointId: endpoint.id });
      return null;
    }

    const { serverUrl, sessionCode, playerName } = endpoint.credentials;
    const wsUrl = serverUrl.replace(/^http/, 'ws');

    return {
      serverUrl: wsUrl,
      sessionId: sessionCode,
      role: 'phone',
      playerName,
      playerId: useAuthStore.getState().user?.sub ?? playerName
    };
  }

  if (endpoint.type === 'discord') {
    if (!isDiscordCredentials(endpoint.credentials)) {
      console.error(`${SERVICE_TAG} Invalid Discord credentials`, { endpointId: endpoint.id });
      return null;
    }

    return {
      channelId: endpoint.credentials.channelId,
      guildId: endpoint.credentials.guildId
    };
  }

  if (endpoint.type === 'standalone') {
    if (!isStandaloneCredentials(endpoint.credentials)) {
      console.error(`${SERVICE_TAG} Invalid Standalone credentials`, { endpointId: endpoint.id });
      return null;
    }

    const { sessionCode, hostName } = endpoint.credentials;
    return {
      serverUrl: WS_URL,
      sessionId: sessionCode,
      role: 'platform',
      playerName: hostName,
      sessionName: endpoint.name
    };
  }

  if (endpoint.type === 'player') {
    if (!isPlayerCredentials(endpoint.credentials)) {
      console.error(`${SERVICE_TAG} Invalid Player credentials`, { endpointId: endpoint.id });
      return null;
    }

    const { sessionCode, playerName } = endpoint.credentials;
    return {
      serverUrl: WS_URL,
      sessionId: sessionCode,
      role: 'phone',
      playerName,
      playerId: useAuthStore.getState().user?.sub ?? playerName,
      sessionName: sessionCode
    };
  }

  console.warn(`${SERVICE_TAG} Unsupported endpoint type`, { type: endpoint.type });
  return null;
}

/**
 * Validate endpoint has required credentials for connection
 */
export function validateEndpoint(endpoint: SavedEndpoint): boolean {
  if (endpoint.type === 'foundry') {
    if (!isFoundryCredentials(endpoint.credentials)) return false;
    const { serverUrl, sessionCode, playerName } = endpoint.credentials;
    return !!(serverUrl && sessionCode && playerName);
  }

  if (endpoint.type === 'discord') {
    if (!isDiscordCredentials(endpoint.credentials)) return false;
    const { channelId, guildId } = endpoint.credentials;
    return !!(channelId && guildId);
  }

  if (endpoint.type === 'standalone') {
    if (!isStandaloneCredentials(endpoint.credentials)) return false;
    const { sessionCode, hostName } = endpoint.credentials;
    return !!(sessionCode && hostName);
  }

  if (endpoint.type === 'player') {
    if (!isPlayerCredentials(endpoint.credentials)) return false;
    return !!(endpoint.credentials.sessionCode && endpoint.credentials.playerName);
  }

  return false;
}
